import cn from 'classnames'
import React, { FC, PropsWithChildren, useMemo } from 'react'
import Banner from '../../../../ui-4.0/components/Banner/Banner'
import ThemeSwitcher from '../../../../ui-4.0/components/ThemeSwitcher/ThemeSwitcher'
import { DownloadLinks, PortalAccountDomain } from '../../../../utils/enums'
import { useAppState, useScreen } from '../../../../utils/hooks'
import Routes from '../../../../utils/routes'
import { scrollRef } from "../../../../utils/scroll"
import { useThemes } from '../../../../utils/themes'
import Footer from '../../../Footer'
import Image from '../../../Presentational/Image'
import { CompactLangSelect } from '../../../Presentational/LangSelect'
import useLiveChat from '../../../Presentational/LiveChat'
import { DownloadButton, QRCode } from '../../Dashboard/Downloads/components'
import qrCode from '../../Dashboard/Downloads/images/QRTT.png'
import styles from './style.module.scss'
import { useTranslation } from 'react-i18next'
import { AppStoreButton, GooglePlayButton } from '../../../../utils/StoresButtons'
import { isAndroid, mobileDevice } from '../../../../utils/constants/browser'

interface BaseViewProps {
    className?: string
    customFooter?: JSX.Element
    stickWarning?: boolean
}

const TTApp = () => {
    const { t } = useTranslation("banners")
    const ls = 'ttApp'
    if (!mobileDevice)
        return <div className={styles.ttAppDesktop}>
            <div className={styles.ttAppText}>
                <div className={styles.title}>{t(`${ls}.title`)}</div>
                <div className={styles.text}>{t(`${ls}.text`)}</div>

            </div>
            <QRCode
                text={false}
                image={qrCode}
            />
        </div>
    else
        return <div className={styles.ttAppMobile}>
            <div className={styles.divider}>
                <span>{t(`${ls}.mobile`)}</span>
            </div>
            <DownloadButton
                image={isAndroid ? <GooglePlayButton /> : <AppStoreButton />}
                link={isAndroid ? DownloadLinks.TTMobileAndroid : DownloadLinks.TTMobileIOS}
            />
        </div>
}

const BaseView: FC<PropsWithChildren<BaseViewProps>> = ({
    className,
    children,
    stickWarning = false,
    customFooter }) => {
    const { portalAccountDomain } = useAppState()
    const { floatLiveChat, fixedLiveChat } = useLiveChat(true)
    const { isMobile } = useScreen()
    const { account, register } = Routes
    const { theme } = useThemes()

    const ja = portalAccountDomain === PortalAccountDomain.TMJP

    const showLangSelector = useMemo(() => {
        return ja
            ? false
            : [
                account.createAccount.live,
                account.createAccount.demo,
                register.live,
                register.ib,
                account.login,
            ].includes(window.location.pathname)
    }, [account, ja, register])

    const showTTApp = false

    const logoImage = useMemo(() => {
        return ja
            ? 'jpLogo'
            : theme === 'dark' ? 'tmLogo' : 'tmLogoLight'
    }, [ja, theme])

    return (
        <div className={cn(styles.baseView)} id="base">
            <Banner />
            <div className={styles.content}>
                <div className={styles.header}>
                    <Image name={logoImage} className={styles.logo} />
                    <div>
                        <ThemeSwitcher />
                        {showLangSelector && !isMobile &&
                            <CompactLangSelect />
                        }
                    </div>
                </div>
                <div className={styles.scrollable} ref={scrollRef}>
                    <div className={cn(styles.mainContent,
                        className)}>
                        {showLangSelector && isMobile &&
                            <div className={styles.lang}>
                                <CompactLangSelect />
                            </div>
                        }
                        {children}
                        {showTTApp && <TTApp />}
                    </div>
                    <Footer
                        customFooter={customFooter}
                        isExtended
                        stickWarning={stickWarning}
                    />
                    {isMobile
                        ? fixedLiveChat
                        : <div className={styles.chat}>
                            {floatLiveChat}
                        </div>}

                </div>
            </div>
        </div>
    )

}

export default BaseView



